import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import apisauce from "apisauce";

// Helper function to convert image URL to base64
const convertImageToBase64 = async (url) => {
  if (!url) return null;
  try {
    const response = await fetch(url, { mode: 'cors' });
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error("Failed to convert image:", error);
    return null;
  }
};

const VoucherPage = () => {
  const { bookingId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [voucherData, setVoucherData] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [autoDownloadAttempted, setAutoDownloadAttempted] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const voucherRef = useRef(null);

  // Fetch voucher data from backend
  useEffect(() => {
    const fetchVoucherData = async () => {
      try {
        const api = apisauce.create({
          baseURL: process.env.REACT_APP_API_BASE_URL,
          timeout: 30000,
        });

        const response = await api.get(`/api/whatsApp-chatbot/booking/voucher/${bookingId}`);
        console.log(response)
        if (response.ok && response.data?.success) {
          const data = response.data.data;

          // Convert images to base64 for PDF generation
          const [hotelLogoBase64, hotelImgBase64, qrCodeBase64, signatureBase64] = await Promise.all([
            convertImageToBase64(data.hotel_logo),
            convertImageToBase64(data.hotel_img),
            convertImageToBase64(data.payment_qr_code_img),
            convertImageToBase64(data.reservation_signature_img),
          ]);

          setVoucherData({
            ...data,
            hotel_logo_base64: hotelLogoBase64,
            hotel_img_base64: hotelImgBase64,
            payment_qr_code_img_base64: qrCodeBase64,
            reservation_signature_img_base64: signatureBase64,
          });
          setImagesLoaded(true);
        } else {
          setError(response.data?.message || "Failed to fetch voucher data");
        }
      } catch (err) {
        setError("Failed to fetch voucher data");
      } finally {
        setLoading(false);
      }
    };

    if (bookingId) {
      fetchVoucherData();
    }
  }, [bookingId]);

  // Auto-download PDF on initial load (after images are loaded)
  useEffect(() => {
    if (voucherData && imagesLoaded && !autoDownloadAttempted && voucherRef.current) {
      setAutoDownloadAttempted(true);
      setTimeout(() => {
        handleDownloadPdf();
      }, 1500);
    }
  }, [voucherData, imagesLoaded, autoDownloadAttempted]);

  const handleDownloadPdf = async () => {
    if (!voucherRef.current) return;

    setDownloading(true);
    try {
      const html2pdf = (await import("html2pdf.js")).default;

      const element = voucherRef.current;
      const opt = {
        margin: [10, 10, 10, 10],
        filename: `booking-voucher-${bookingId}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          logging: false,
          scrollY: 0,
          windowWidth: 630,
        },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        pagebreak: { mode: ["css", "legacy"] },
      };

      await html2pdf().set(opt).from(element).save();
    } catch (err) {
      console.error("PDF generation failed:", err);
    } finally {
      setDownloading(false);
    }
  };

  const toTitleCase = (str) => {
    if (!str) return "";
    return str
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const getUpdatedStatus = (status) => {
    const statusMap = {
      confirmed: "Confirmed",
      pending: "Pending",
      cancelled: "Cancelled",
      checked_in: "Checked In",
      checked_out: "Checked Out",
      no_show: "No Show",
    };
    return statusMap[status] || status || "";
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="#E6E6FA"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="#E6E6FA"
      >
        <Typography variant="h5" color="error" gutterBottom>
          {error}
        </Typography>
        <Typography variant="body1">
          Please check the booking ID and try again.
        </Typography>
      </Box>
    );
  }

  const data = voucherData;

  // Build house rules HTML
  let houseRulesHTML = "";
  if (data?.house_rules?.length) {
    const rulesList = data.house_rules.map((rule) => `<li style="color:#777777;">${rule}</li>`).join("");
    houseRulesHTML = `
      <div class="no-break" style="margin-bottom:20px; color:#777777;">
        <p style="color:#777777;"><b>House Rules:</b></p>
        <ul style="margin:0; padding-left:20px; color:#777777;">
          ${rulesList}
        </ul>
      </div>
    `;
  }

  // Build cancellation policy HTML
  let cancellationPolicyHTML = "";
  if (data?.cancellation_policy?.length) {
    const policyList = data.cancellation_policy
      .map((policy) => `<li style="color:#777777;">${policy}</li>`)
      .join("");
    cancellationPolicyHTML = `
      <div class="no-break" style="margin-bottom:20px; color:#777777;">
        <p style="color:#777777;"><b>Cancellation Policy:</b></p>
        <ul style="margin:0; padding-left:20px; color:#777777;">
          ${policyList}
        </ul>
      </div>
    `;
  }

  // Build caution HTML
  let cautionHTML = "";
  if (data?.cautions?.length) {
    const cautionList = data.cautions.map((item) => `<li style="color:#777777;">${item}</li>`).join("");
    cautionHTML = `
      <div class="no-break" style="margin-bottom:20px; color:#777777;">
        <p style="color:#777777;"><b>For Your Safety and Comfort:</b></p>
        <ul style="margin:0; padding-left:20px; color:#777777;">
          ${cautionList}
        </ul>
      </div>
    `;
  }

  // Build bank details HTML
  let bankDetailsHTML = "";
  if (data?.bank_details) {
    const rows = [];
    if (data?.bank_details?.acc_holder_name) {
      rows.push(`<div class="detail-row"><span class="detail-label">Beneficiary Name:</span><span>${data.bank_details.acc_holder_name}</span></div>`);
    }
    if (data?.bank_details?.acc_no) {
      rows.push(`<div class="detail-row"><span class="detail-label">Account No:</span><span>${data.bank_details.acc_no}</span></div>`);
    }
    if (data?.bank_details?.ifsc) {
      rows.push(`<div class="detail-row"><span class="detail-label">RTGS/NEFT/IFSC:</span><span>${data.bank_details.ifsc}</span></div>`);
    }
    if (data?.bank_details?.bank_name) {
      rows.push(`<div class="detail-row"><span class="detail-label">Bank Name:</span><span>${data.bank_details.bank_name}</span></div>`);
    }
    if (data?.bank_details?.branch_name) {
      rows.push(`<div class="detail-row"><span class="detail-label">Branch:</span><span>${data.bank_details.branch_name}</span></div>`);
    }
    if (data?.bank_details?.acc_type) {
      rows.push(`<div class="detail-row"><span class="detail-label">Account Type:</span><span>${data.bank_details.acc_type}</span></div>`);
    }
    if (rows.length) {
      bankDetailsHTML = rows.join("");
    }
  }

  // Build rooms HTML
  const roomsHTML = data?.updated_rooms
    ?.map((e) => {
      let html = `<div class="detail-row"><span class="detail-label">Room Type:</span><span>${e?.roomtype}</span></div>`;
      if (e?.total_rooms) {
        html += `<div class="detail-row"><span class="detail-label">Total Rooms:</span><span>${e?.total_rooms}</span></div>`;
      }
      html += `<div class="detail-row"><span class="detail-label">Meal Plan:</span><span>${e?.meal_plan}</span></div>`;
      if (!data?.is_custom_price) {
        html += `<div class="detail-row"><span class="detail-label">Rate:</span><span>INR ${e?.total_amount}</span></div>`;
      }
      return html;
    })
    .join("");

  const voucherHTML = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1">
      <style>
        * {
          box-sizing: border-box;
          -webkit-text-size-adjust: 100%;
          -ms-text-size-adjust: 100%;
        }
        body {
          margin: 0;
          padding: 0;
          font-family: Open Sans, Helvetica, Arial, sans-serif;
          font-size: 13px;
          line-height: 1.5;
          color: #000000;
          background-color: #ffffff;
          word-wrap: break-word;
          overflow-wrap: break-word;
        }
        img {
          max-width: 100%;
          height: auto;
          border: 0;
          display: block;
        }
        .voucher-wrapper {
          max-width: 600px;
          margin: 0 auto;
          background-color: #f2f2f2;
        }
        .no-break {
          break-inside: avoid;
          page-break-inside: avoid;
        }
        .section {
          padding: 20px;
        }
        .detail-row {
          display: flex;
          border-top: 1px solid #ccc;
          padding: 5px;
          break-inside: avoid;
          page-break-inside: avoid;
          color: #000000;
        }
        .detail-row:first-child {
          border-top: none;
        }
        .detail-label {
          width: 200px;
          min-width: 200px;
          color: #000000;
        }
        .card-no-border {
          background: #fafafa;
          border-radius: 10px;
          overflow: hidden;
          font-size: small;
          font-family: Open Sans, Helvetica, Arial, sans-serif;
          color: #000000;
        }
        .card-bordered {
          background: #fafafa;
          border-radius: 10px;
          overflow: hidden;
          font-size: small;
          font-family: Open Sans, Helvetica, Arial, sans-serif;
          line-height: 1.4;
          color: #000000;
          border: 1px solid #e0e0e0;
        }
        .section-title {
          font-size: small;
          color: #777777;
          font-family: Open Sans, Helvetica, Arial, sans-serif;
          margin: 0 0 8px 0;
        }
        .guests-inner {
          display: flex;
          flex-direction: column;
          width: 100%;
        }
        .guests-row {
          display: flex;
          padding: 5px;
          color: #000000;
        }
        .guests-row-bordered {
          border-bottom: 1px solid #ccc;
        }
        .guests-label {
          width: 150px;
          color: #000000;
        }
      </style>
    </head>
    <body>
      <div class="voucher-wrapper">

        <!-- Header -->
        <div class="no-break" style="background-color:#9b9b9b; text-align:center; padding:10px; border-radius:15px 15px 0 0;">
          <h1 style="font-size:20px; font-weight:800; margin:0; color:#ffffff !important; text-transform:uppercase;">
            Booking Confirmation
          </h1>
        </div>

        <!-- Hotel Logo -->
        ${(data?.hotel_logo_base64 || data?.hotel_logo) ? `
        <div class="no-break" style="padding:0; margin:0;">
          <img src="${data.hotel_logo_base64 || data.hotel_logo}" alt="Hotel Logo" style="width:100%; height:auto; max-height:300px; display:block; border:none;" crossorigin="anonymous">
        </div>
        ` : ""}

        <!-- Hotel Image -->
        ${(data?.hotel_img_base64 || data?.hotel_img) ? `
        <div class="no-break" style="padding:0; margin:0;">
          <img src="${data.hotel_img_base64 || data.hotel_img}" alt="Hotel Image" style="width:100%; height:auto; max-height:300px; display:block; border:none;" crossorigin="anonymous">
        </div>
        ` : ""}

        <!-- Greeting -->
        <div class="no-break section" style="font-size:14px; line-height:20px; color:#000000;">
          <p style="color:#000000;">Dear <b>${data?.guest_name}</b>,</p>
          <p style="color:#000000;">Greetings from ${toTitleCase(data?.hotel_name)} !!</p>
          <p style="color:#000000;">Thank you for choosing ${toTitleCase(data?.hotel_name)} for your upcoming requirement. We are delighted to confirm the availability for your reservation and provide you with the details of your booking. Please review the following information:</p>
        </div>

        <!-- Reservation Details -->
        <div class="section" style="color:#000000;">
          <p class="section-title"><b>Reservation Details:</b></p>
          <div class="card-no-border">
            <div class="no-break">
              <div class="detail-row" style="border-top:none;"><span class="detail-label">Booking ID:</span><span>${data?.booking_id || ""}</span></div>
              <div class="detail-row"><span class="detail-label">Guest Name:</span><span>${data?.guest_name}</span></div>
              <div class="detail-row"><span class="detail-label">Check in Date:</span><span>${data?.checkin_date}</span></div>
              <div class="detail-row"><span class="detail-label">Check out Date:</span><span>${data?.checkout_date}</span></div>
            </div>
            <div class="no-break">
              <div class="detail-row" style="align-items:flex-start;">
                <span class="detail-label" style="padding-top:5px;">No: of Guests:</span>
                <span style="flex:1;">
                  <div class="guests-inner">
                    <div class="guests-row guests-row-bordered">
                      <span class="guests-label">Adults</span>
                      <span>${data?.no_of_adults}</span>
                    </div>
                    <div class="guests-row">
                      <span class="guests-label">Child</span>
                      <span>${data?.no_of_childs}</span>
                    </div>
                  </div>
                </span>
              </div>
            </div>
            ${roomsHTML ? `<div class="no-break">${roomsHTML}</div>` : ""}
            <div class="no-break">
              <div class="detail-row"><span class="detail-label">Extra Mattress:</span><span>${data?.extra_mattress ?? 0}</span></div>
              <div class="detail-row"><span class="detail-label">Status / Cut-off Date:</span><span>${getUpdatedStatus(data?.status)}${data?.cut_off_date ? ` / ${data?.cut_off_date}` : ""}</span></div>
              ${data?.billing_instruction ? `<div class="detail-row"><span class="detail-label">Billing Instruction:</span><span>${data?.billing_instruction}</span></div>` : ""}
            </div>
          </div>
        </div>

        <!-- Payment Information -->
        <div class="section" style="color:#000000;">
          <p class="section-title"><b>Payment Information:</b></p>
          <div class="no-break card-bordered" style="padding:10px;">
            <div>
              <span style="font-weight:600; color:#000000;">Total Amount:</span>
              <span style="color:#000000;"> INR ${data?.total_price} /-</span>
            </div>
            ${data?.paid_amount ? `
            <div>
              <span style="font-weight:600; color:#000000;">Paid Amount:</span>
              <span style="color:#000000;"> INR ${data?.paid_amount} /-</span>
            </div>
            ` : ""}
            <div style="font-weight:600; padding:10px 0 4px 0; color:#000000;">Rate Includes:</div>
            <div style="padding:0 0 0 15px;">
              <ul style="margin:0; padding:0 0 0 15px; color:#000000;">
                ${data?.meal_plans ? `<li style="color:#000000;">Meal plan details - ${data?.meal_plans}</li>` : ""}
                <li style="color:#000000;">All taxes</li>
              </ul>
            </div>
          </div>

          ${data?.is_payment_details_available ? `
          <p class="section-title" style="margin-top:14px;"><b>Payment Method:</b></p>
          <p style="margin-top:10px; font-size:small; color:#000000;">
            We request you to make all payments through NEFT/RTGS for which the details are mentioned below :
          </p>
          <div class="no-break" style="background:#fafafa; border-radius:10px; overflow:hidden; font-size:small; font-family:Open Sans, Helvetica, Arial, sans-serif; width:100%; border:1px solid #e0e0e0;">
            <div style="display:${data?.payment_qr_code_img ? "flex" : "block"}; align-items:stretch; width:100%;">
              <div style="flex:1; padding:16px;">
                <div style="background-color:#9b9b9b; color:#fff; font-weight:bold; text-align:center; padding:8px; border-radius:6px 6px 0 0; margin-bottom:12px;">
                  Bank Account Details
                </div>
                ${bankDetailsHTML}
              </div>
              ${(data?.payment_qr_code_img_base64 || data?.payment_qr_code_img) ? `
              <div style="width:200px; padding:16px; display:flex; align-items:center; justify-content:center; background:#fff; border-left:1px solid #e0e0e0;">
                <img src="${data.payment_qr_code_img_base64 || data.payment_qr_code_img}" alt="QR Code" style="width:100%; height:auto; object-fit:contain; border-radius:6px;" crossorigin="anonymous">
              </div>
              ` : ""}
            </div>
          </div>
          ${data?.payment_link ? `
          <p style="margin-top:10px; font-size:small;">
            Or online using our secure payment link:
            <a href="${data?.payment_link}" target="_blank">${data?.payment_link}</a>
          </p>
          ` : ""}
          ` : ""}
        </div>

        <!-- Important Information -->
        <div class="section" style="font-size:small; font-family:Open Sans, Helvetica, Arial, sans-serif; color:#777777;">
          <div class="no-break" style="margin-bottom:20px; color:#777777;">
            <p style="color:#777777;"><b>Please note the following important information:</b></p>
            <ul style="margin:0; padding-left:20px; color:#777777;">
              ${data?.guest_reporting_time ? `<li style="color:#777777;">Reporting time: ${data?.guest_reporting_time || "-"}</li>` : ""}
              ${data?.guest_checkin_time ? `<li style="color:#777777;">Check in time: ${data?.guest_checkin_time || "-"}</li>` : ""}
              ${data?.guest_cruise_start_time ? `<li style="color:#777777;">Cruise start time: ${data?.guest_cruise_start_time || "-"}</li>` : ""}
              ${data?.guest_checkout_time ? `<li style="color:#777777;">Check Out time: ${data?.guest_checkout_time || "-"}</li>` : ""}
              <li style="color:#777777;">The Tariff is in Indian Rupees.</li>
            </ul>
          </div>

          ${cautionHTML}
          ${cancellationPolicyHTML}
          ${houseRulesHTML}

          <div class="no-break">
            <p style="color:#777777;">Should you have any questions or require further assistance, please do not hesitate to contact us at
              <b>${data?.reservation_mobile || "-"}</b>. We will be happy to assist you.
            </p>
            <p style="color:#777777;">We look forward to welcoming you to ${data?.hotel_name} and providing you with a memorable experience.
              Thank you again for choosing us.</p>
            <p style="font-style:italic; color:#777777;">Thanks & Regards</p>
            ${(data?.reservation_signature_img_base64 || data?.reservation_signature_img) ? `
            <p style="margin:0;">
              <img src="${data.reservation_signature_img_base64 || data.reservation_signature_img}" alt="Signature" style="max-width:250px; height:auto; display:block;" crossorigin="anonymous">
            </p>
            ` : ""}
          </div>
        </div>

      </div>
    </body>
    </html>
  `;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#E6E6FA",
        position: "relative",
      }}
    >
      {/* Download Button - Fixed at top */}
      <Box
        sx={{
          position: "fixed",
          top: 20,
          right: 20,
          zIndex: 1000,
        }}
      >
        <Button
          variant="contained"
          color="primary"
          startIcon={downloading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
          onClick={handleDownloadPdf}
          disabled={downloading}
          sx={{
            backgroundColor: "#9b9b9b",
            "&:hover": {
              backgroundColor: "#7a7a7a",
            },
          }}
        >
          {downloading ? "Downloading..." : "Download PDF"}
        </Button>
      </Box>

      {/* Voucher Content */}
      <Box
        ref={voucherRef}
        dangerouslySetInnerHTML={{ __html: voucherHTML }}
      />
    </Box>
  );
};

export default VoucherPage;
